import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportIPIPMilterOptions } from "../report-ipipm";
import { ReportIPIPMFilterSettingsForm } from "../settings-form/ReportIPIPMFilterSettingsForm";
import { ReportIPIPMFilterSettings } from "../settings/ReportIPIPMFilterSettings";
import { ReportIPIPMRow } from "../table-data/ReportIPIPMRow";
import { ReportIPIPMJournalRequest } from "../journal/ReportIPIPMJournalRequest";
import { Option } from "@/modules/budget/monitoring/reports-constructor/common/types/Option";
import { ReportIPIPMTableDataRequest } from "../data-request/ReportIPIPMTableDataRequest";

export class ReportIPIPMFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportIPIPMilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'IPIPM') {
            throw new Error('не соответсвующий подтип FilterSettings')
        }
        if (options.key !== 'IPIPM') {
            throw new Error('не соответсвующий подтип FilterOptions')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('Не найден dataSource')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_unit_option')
        }
        measureUnitOption = { ...measureUnitOption }


        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        // 2. Формирование
        const form: ReportIPIPMFilterSettingsForm = {
            // Base:
            key: 'IPIPM',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            periodFrom: new Date(settings.period.from),
            periodTo: new Date(settings.period.to),
            regions: regions,
            measureUnit: measureUnitOption,
            segmentations: cloneDeep(settings.segmentations),
            budgetTypes: budgetTypeOptions,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)!,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            statuses: statusOptions,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            isTree: settings.isTree,
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators)
        };

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'IPIPM') {
            throw new Error('не соответсвующий подтип FilterSettingsForm')
        }

        if ((!form.periodTo) || (!form.periodFrom)) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.segmentations?.length) {
            throw new Error('error_input_segmentation')
        }

        if (!form.regions?.length) {
            throw new Error('error_input_region')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (form.mutuallyRedeemingLevels.length === 0) {
            throw new Error('error_input_mutually_redeeming_levels')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')

        const settings: ReportIPIPMFilterSettings = {
            // Base:
            reportName: 'IPIPM',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            period: { from: strPeriodFrom, to: strPeriodTo },
            regions: form.regions.map(it => it.value),
            measureUnit: form.measureUnit.value,
            segmentations: form.segmentations,
            budgetTypes: form.budgetTypes.map(it => it.value),
            mutuallyRedeeming: form.mutuallyRedeeming.value!,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            statuses: form.statuses.map(it => it.value),
            roundUpTo: roundUpTo,
            isTree: form.isTree,
            expenses: cloneDeep(form.expenses),
            indicators: cloneDeep(form.indicators)
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportIPIPMilterOptions)

        const todayDate = moment().toDate()
        const firstJan = moment().startOf('year').toDate()
        const emptyForm: ReportIPIPMFilterSettingsForm = {
            key: 'IPIPM',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'IPIPM')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'EXPENSES')!,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'PERIOD')!,
            // Custom fields
            periodFrom: firstJan,
            periodTo: todayDate,
            regions: [],
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            segmentations: cloneDeep(options.segmentations),
            budgetTypes: [],
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITH')!,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            statuses: options.statuses.filter(it => it.value === '39'),
            roundUp: false,
            roundUpTo: null,
            isTree: true,
            expenses: cloneDeep(options.expenses),
            indicators: cloneDeep(options.indicators)
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'IPIPM') {
            throw new Error('не соответсвующий подтип FilterSettingsForm')
        }

        if ((!form.periodTo) || (!form.periodFrom)) {
            throw new Error('error_input_date')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }
        if (!form.regions?.length) {
            throw new Error('error_input_region')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.mutuallyRedeemingLevels.length === 0) {
            throw new Error('error_input_mutually_redeeming_levels')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        if (form.dataSource?.value !== 'LOADER') {
            throw new Error('error_unsupported_data_source')
        }
        const strPeriodFrom = moment(form.periodFrom).format('YYYY-MM-DD')
        const strPeriodTo = moment(form.periodTo).format('YYYY-MM-DD')


        const vzkList = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })
        if (!form.segmentations?.length) {
            throw new Error('error_input_segmentation')
        }

        const segmentations: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)

        const params: ReportIPIPMTableDataRequest = {
            user_id: userId,
            source: form.dataSource.value,
            period_from: strPeriodFrom,
            period_to: strPeriodTo,
            regions: form.regions.map(r => r.value),
            measure_unit: form.measureUnit.value,
            segmentations: segmentations,
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: vzkList,
            status_list: form.statuses.map(it => it.value),
            round_up_to: roundUpTo,
            is_tree: form.isTree,
            locale: i18nService.locale,
            expenses: form.expenses
        }

        const indicatorsOrder: string[] = form.indicators.filter(it => it.active).map(it => it.key)

        const response = await fetch('/api-py/monitoring/reports-constructor/IPIPM',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('Не удалось получить данные для отчета')
        }
        const responseTableData: any[] = await response.json()
        const tableData: ReportIPIPMRow[] = responseTableData.map((row: any, index) => {
            row.index = index
            return row
        })

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportIPIPMJournalRequest = {
            reportName: "IPIPM",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            indicators_order: indicatorsOrder, // Может быть редактирована во время превью
            params: params,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
